import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Play, 
  Users, 
  Radio, 
  Plus, 
  Eye, 
  Clock, 
  TrendingUp,
  Zap,
  Globe,
  BarChart3
} from 'lucide-react';
import { useStream } from '../context/StreamContext';
import StreamCard from './StreamCard';
import StatsWidget from './StatsWidget';

function Dashboard() {
  const { 
    streams, 
    activeStreams, 
    isLoading, 
    error, 
    createStream 
  } = useStream();
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [streamName, setStreamName] = useState('');

  const totalViewers = streams.reduce((sum, stream) => sum + (stream.viewers || 0), 0);
  const totalDuration = streams.reduce((sum, stream) => sum + (stream.duration || 0), 0);

  const handleCreateStream = async (e) => {
    e.preventDefault();
    if (!streamName.trim()) return;
    
    try {
      await createStream({ 
        name: streamName,
        meta: { 
          createdBy: 'dashboard',
          category: 'general' 
        }
      });
      setStreamName('');
      setShowCreateModal(false);
    } catch (error) {
      console.error('Failed to create stream:', error);
    }
  };

  if (isLoading && streams.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cf-orange border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading streams...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">
            Welcome to CloudStream
          </h1>
          <p className="text-gray-400">
            Manage your multi-host streaming platform powered by Cloudflare
          </p>
        </div>
        
        <div className="flex items-center space-x-4 mt-4 md:mt-0">
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Create Stream</span>
          </button>
          
          <Link to="/host" className="btn-secondary flex items-center space-x-2">
            <Radio className="w-5 h-5" />
            <span>Start Hosting</span>
          </Link>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsWidget
          title="Total Streams"
          value={streams.length}
          icon={Radio}
          color="blue"
          subtitle={`${activeStreams.length} live now`}
        />
        
        <StatsWidget
          title="Total Viewers"
          value={totalViewers}
          icon={Users}
          color="green"
          subtitle="Across all streams"
        />
        
        <StatsWidget
          title="Watch Time"
          value={`${Math.floor(totalDuration / 3600)}h ${Math.floor((totalDuration % 3600) / 60)}m`}
          icon={Clock}
          color="purple"
          subtitle="Total duration"
        />
        
        <StatsWidget
          title="Performance"
          value="99.9%"
          icon={Zap}
          color="orange"
          subtitle="Uptime this month"
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link to="/host" className="card hover:bg-gray-750 transition-all duration-200 group">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-cf-blue rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
              <Radio className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Host a Stream</h3>
              <p className="text-gray-400">Start broadcasting to your audience</p>
            </div>
          </div>
        </Link>

        <Link to="/viewer" className="card hover:bg-gray-750 transition-all duration-200 group">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
              <Play className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Watch Streams</h3>
              <p className="text-gray-400">Browse and watch live content</p>
            </div>
          </div>
        </Link>

        <Link to="/restream" className="card hover:bg-gray-750 transition-all duration-200 group">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
              <Globe className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Restream</h3>
              <p className="text-gray-400">Broadcast to multiple platforms</p>
            </div>
          </div>
        </Link>
      </div>

      {/* Live Streams Section */}
      {activeStreams.length > 0 && (
        <div>
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
            <h2 className="text-2xl font-bold text-white">Live Now</h2>
            <span className="bg-red-500 text-white px-2 py-1 rounded-full text-sm">
              {activeStreams.length}
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {activeStreams.map((stream) => (
              <StreamCard key={stream.id} stream={stream} showActions />
            ))}
          </div>
        </div>
      )}

      {/* All Streams Section */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">All Streams</h2>
          <div className="flex items-center space-x-2 text-gray-400">
            <BarChart3 className="w-5 h-5" />
            <span>{streams.length} total</span>
          </div>
        </div>
        
        {streams.length === 0 ? (
          <div className="card text-center py-12">
            <Radio className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-300 mb-2">No streams yet</h3>
            <p className="text-gray-500 mb-6">Create your first stream to get started</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
            >
              Create Your First Stream
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {streams.map((stream) => (
              <StreamCard key={stream.id} stream={stream} showActions />
            ))}
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-500 bg-opacity-10 border border-red-500 rounded-lg p-4">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Create Stream Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-white mb-4">Create New Stream</h3>
            
            <form onSubmit={handleCreateStream}>
              <div className="mb-4">
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Stream Name
                </label>
                <input
                  type="text"
                  value={streamName}
                  onChange={(e) => setStreamName(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-cf-orange focus:border-transparent"
                  placeholder="Enter stream name..."
                  autoFocus
                />
              </div>
              
              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={!streamName.trim()}
                  className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create Stream
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard; 